<template>
  <ion-page>
    <ion-content class="welcome-content">
      <div class="background-scene">
        <div class="stars"></div>
      </div>
      
      <div class="transaction-header">
        <ion-button fill="clear" class="back-button" @click="goBack">
          <ion-icon :icon="chevronBack"></ion-icon>
          Back
        </ion-button>
        <h1 class="transaction-title">{{ currentEvent ? 'Edit Transaction' : 'Add Transaction' }}</h1>
      </div>

      <!-- Loading indicator for event -->
      <div v-if="isLoadingEvent" class="loading-container">
        <div class="loading-spinner"></div>
        <p class="loading-text">Loading event details...</p>
      </div>

      <form @submit.prevent="saveEvent" class="transaction-form" v-else>
        <!-- Step 1: Category Selection (Always shown) -->
        <div class="transaction-step" :class="{ 'step-completed': newEvent.category }">
          <div class="step-header">
            <div class="step-number">1</div>
            <h3>Select Transaction Category</h3>
          </div>
          
          <div class="step-content">
            <div class="transaction-input-group">
              <ion-label>Category</ion-label>
              <select 
                v-model="newEvent.category" 
                class="transaction-custom-input"
                @change="handleCategoryChange($event.target.value)"
              >
                <option value="" disabled selected>Select category</option>
                <option value="MORTGAGE">Mortgage</option>
                <option value="RENT">Rent</option>
                <option value="GROCERY">Grocery</option>
                <option value="DINING">Dining</option>
                <option value="ENTERTAINMENT">Entertainment</option>
                <option value="UTILITY">Utilities</option>
                <option value="SUBSCRIPTION">Subscription</option>
                <option value="INSURANCE">Insurance</option>
                <option value="GENERIC_LOAN">Generic Loan</option>
                <option value="AUTO_LOAN">Auto Loan</option>
                <option value="CREDIT_CARD">Credit Card</option>
                <option value="PHONE">Phone</option>
                <option value="SAVINGS">Savings</option>
                <option value="PRIMARY_INCOME">Primary Income</option>
                <option value="SECONDARY_INCOME">Secondary Income</option>
                <option value="MISC">Misc Income</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Step 2: Loan Details (Only for loan categories) -->
        <div v-if="isLoanCategory" class="transaction-step" :class="{ 'step-completed': loanCalculationPreview }">
          <div class="step-header">
            <div class="step-number">2</div>
            <h3>Loan Details</h3>
          </div>
          
          <div class="step-content">
            <div class="loan-form-layout">
              <!-- All Input Fields - Full Width -->
              <div class="loan-fields-column">
                <div class="loan-fields-grid">
                  <div class="transaction-input-group">
                    <ion-label>Name</ion-label>
                    <input 
                      type="text" 
                      v-model="newEvent.name" 
                      placeholder="Enter loan name" 
                      required
                      class="transaction-custom-input"
                    />
                  </div>

                  <div class="transaction-input-group">
                    <ion-label>Description</ion-label>
                    <input 
                      type="text" 
                      v-model="newEvent.description" 
                      placeholder="Enter loan description" 
                      required
                      class="transaction-custom-input"
                    />
                  </div>

                  <div class="transaction-input-group">
                    <ion-label>Total Loan Amount ($)</ion-label>
                    <input 
                      type="text" 
                      :value="formattedAmount" 
                      @input="onAmountInput"
                      @blur="onAmountBlur"
                      @invalid="onAmountInvalid"
                      placeholder="Enter total loan amount" 
                      inputmode="decimal"
                      required
                      class="transaction-custom-input"
                    />
                  </div>

                  <div class="transaction-input-group">
                    <ion-label>Interest Rate (%) — required</ion-label>
                    <input
                      type="number"
                      v-model="newEvent.interest"
                      placeholder="Enter interest rate (required)"
                      step="0.001"
                      inputmode="decimal"
                      @blur="onDecimalBlur('interest', $event)"
                      @invalid="onInterestInvalid"
                      required
                      class="transaction-custom-input"
                    />
                  </div>

                  <div class="transaction-input-group">
                    <ion-label>Additional Principal Payment ($) — optional</ion-label>
                    <input
                      type="number"
                      v-model="newEvent.principal"
                      placeholder="Enter additional monthly principal payment"
                      step="0.01"
                      inputmode="decimal"
                      @blur="onDecimalBlur('principal', $event)"
                      @invalid="onPrincipalInvalid"
                      class="transaction-custom-input"
                    />
                  </div>

                  <div v-if="newEvent.category === 'MORTGAGE'" class="transaction-input-group">
                    <ion-label>Escrow Payment ($)</ion-label>
                    <input
                      type="number"
                      v-model="newEvent.escrow"
                      placeholder="Enter monthly escrow payment (taxes, insurance, etc.)"
                      step="0.01"
                      inputmode="decimal"
                      @blur="onDecimalBlur('escrow', $event)"
                      class="transaction-custom-input"
                    />
                  </div>

                  <div class="transaction-input-group">
                    <ion-label>Scenario</ion-label>
                    <select 
                      v-model="newEvent.scenarioID" 
                      class="transaction-custom-input"
                    >
                      <option value="" disabled selected>Select scenario</option>
                      <option 
                        v-for="scenario in scenarios" 
                        :key="scenario.id" 
                        :value="scenario.id"
                      >
                        {{ scenario.name }}
                      </option>
                    </select>
                  </div>

                  <div class="transaction-input-group">
                    <ion-label>Start Date</ion-label>
                    <input 
                      type="date" 
                      v-model="newEvent.startDate" 
                      :min="minDate"
                      :max="maxDate"
                      required
                      class="transaction-custom-input"
                    />
                  </div>

                  <div class="transaction-input-group">
                    <ion-label>Frequency</ion-label>
                    <select 
                      v-model="newEvent.frequency" 
                      class="transaction-custom-input"
                    >
                      <option value="ONCE">Once</option>
                      <option value="DAILY">Daily</option>
                      <option value="EVERY_OTHER_DAY">Every Other Day</option>
                      <option value="WEEKDAYS_ONLY">Weekdays Only</option>
                      <option value="WEEKENDS_ONLY">Weekends Only</option>
                      <option value="WEEKLY">Weekly</option>
                      <option value="EVERY_OTHER_WEEK">Every Other Week</option>
                      <option value="MONTHLY">Monthly</option>
                      <option value="EVERY_OTHER_MONTH">Every Other Month</option>
                      <option value="TWICE_PER_YEAR">Twice Per Year</option>
                      <option value="YEARLY">Yearly</option>
                    </select>
                  </div>

                  <div class="transaction-input-group">
                    <ion-label>Type</ion-label>
                    <select 
                      v-model="newEvent.type" 
                      class="transaction-custom-input"
                    >
                      <option value="CREDIT">Income</option>
                      <option value="DEBIT">Expense</option>
                    </select>
                  </div>

                  <div v-if="newEvent.category === 'MORTGAGE'" class="transaction-input-group">
                    <ion-label>Term</ion-label>
                    <select 
                      v-model="newEvent.loanTerm" 
                      class="transaction-custom-input"
                      @change="onTermChange"
                    >
                      <option value="" disabled selected>Select term</option>
                      <option value="12">12 months (1 year)</option>
                      <option value="24">24 months (2 years)</option>
                      <option value="36">36 months (3 years)</option>
                      <option value="48">48 months (4 years)</option>
                      <option value="60">60 months (5 years)</option>
                      <option value="72">72 months (6 years)</option>
                      <option value="84">84 months (7 years)</option>
                      <option value="96">96 months (8 years)</option>
                      <option value="108">108 months (9 years)</option>
                      <option value="120">120 months (10 years)</option>
                      <option value="132">132 months (11 years)</option>
                      <option value="144">144 months (12 years)</option>
                      <option value="156">156 months (13 years)</option>
                      <option value="168">168 months (14 years)</option>
                      <option value="180">180 months (15 years)</option>
                      <option value="192">192 months (16 years)</option>
                      <option value="204">204 months (17 years)</option>
                      <option value="216">216 months (18 years)</option>
                      <option value="228">228 months (19 years)</option>
                      <option value="240">240 months (20 years)</option>
                      <option value="252">252 months (21 years)</option>
                      <option value="264">264 months (22 years)</option>
                      <option value="276">276 months (23 years)</option>
                      <option value="288">288 months (24 years)</option>
                      <option value="300">300 months (25 years)</option>
                      <option value="312">312 months (26 years)</option>
                      <option value="324">324 months (27 years)</option>
                      <option value="336">336 months (28 years)</option>
                      <option value="348">348 months (29 years)</option>
                      <option value="360">360 months (30 years)</option>
                    </select>
                  </div>

                  <div v-else class="transaction-input-group">
                    <ion-label>Term</ion-label>
                    <select 
                      v-model="newEvent.loanTerm" 
                      class="transaction-custom-input"
                      @change="onTermChange"
                    >
                      <option value="" disabled selected>Select term</option>
                      <option value="12">12 months (1 year)</option>
                      <option value="24">24 months (2 years)</option>
                      <option value="36">36 months (3 years)</option>
                      <option value="48">48 months (4 years)</option>
                      <option value="60">60 months (5 years)</option>
                      <option value="72">72 months (6 years)</option>
                      <option value="84">84 months (7 years)</option>
                      <option value="96">96 months (8 years)</option>
                      <option value="108">108 months (9 years)</option>
                      <option value="120">120 months (10 years)</option>
                      <option value="132">132 months (11 years)</option>
                      <option value="144">144 months (12 years)</option>
                      <option value="156">156 months (13 years)</option>
                      <option value="168">168 months (14 years)</option>
                      <option value="180">180 months (15 years)</option>
                      <option value="192">192 months (16 years)</option>
                      <option value="204">204 months (17 years)</option>
                      <option value="216">216 months (18 years)</option>
                      <option value="228">228 months (19 years)</option>
                      <option value="240">240 months (20 years)</option>
                      <option value="252">252 months (21 years)</option>
                      <option value="264">264 months (22 years)</option>
                      <option value="276">276 months (23 years)</option>
                      <option value="288">288 months (24 years)</option>
                      <option value="300">300 months (25 years)</option>
                      <option value="312">312 months (26 years)</option>
                      <option value="324">324 months (27 years)</option>
                      <option value="336">336 months (28 years)</option>
                      <option value="348">348 months (29 years)</option>
                      <option value="360">360 months (30 years)</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Loan Calculation Details - Below Inputs -->
              <div v-if="loanCalculationPreview" class="loan-preview-card">
                <h4>Loan Calculation Details</h4>
                <div class="loan-preview-details">
                  <div class="loan-preview-item">
                    <span class="loan-preview-label">Standard Payment:</span>
                    <span class="loan-preview-value">{{ formatCurrency(loanCalculationPreview.standardPayment) }}</span>
                  </div>
                  <div v-if="newEvent.category === 'MORTGAGE'" class="loan-preview-item total-payment">
                    <span class="loan-preview-label">Total Monthly Payment:</span>
                    <span class="loan-preview-value" style="color: #4CAF50; font-size: 1.1em;">{{ formatCurrency((loanCalculationPreview.monthlyPayment || 0) + (parseFloat(newEvent.escrow) || 0)) }}</span>
                  </div>
                  <div v-if="newEvent.category === 'AUTO_LOAN' || newEvent.category === 'GENERIC_LOAN'" class="loan-preview-item total-payment">
                    <span class="loan-preview-label">Total Monthly Payment:</span>
                    <span class="loan-preview-value" style="color: #4CAF50; font-size: 1.1em;">{{ formatCurrency((loanCalculationPreview.standardPayment || 0) + (loanCalculationPreview.additionalPrincipal || 0)) }}</span>
                  </div>
                  <div class="loan-preview-item">
                    <span class="loan-preview-label">Additional Principal:</span>
                    <span class="loan-preview-value">{{ formatCurrency(loanCalculationPreview.additionalPrincipal) }}</span>
                  </div>
                  <div v-if="newEvent.category === 'MORTGAGE'" class="loan-preview-item">
                    <span class="loan-preview-label">Escrow Payment:</span>
                    <span class="loan-preview-value">{{ formatCurrency(parseFloat(newEvent.escrow) || 0) }}</span>
                  </div>
                  <div class="loan-preview-item">
                    <span class="loan-preview-label">Loan Term:</span>
                    <span class="loan-preview-value">{{ loanCalculationPreview.totalPayments }} months</span>
                  </div>
                  <div class="loan-preview-item">
                    <span class="loan-preview-label">End Date:</span>
                    <span class="loan-preview-value">{{ formatDate(loanCalculationPreview.endDate) }}</span>
                  </div>
                  <div class="loan-preview-item">
                    <span class="loan-preview-label">Total Interest:</span>
                    <span class="loan-preview-value">{{ formatCurrency(loanCalculationPreview.totalInterest) }}</span>
                  </div>
                  <div class="loan-preview-item">
                    <span class="loan-preview-label">Total Payed Over Term:</span>
                    <span class="loan-preview-value">{{ formatCurrency(loanCalculationPreview.monthlyPayment * loanCalculationPreview.totalPayments) }}</span>
                  </div>
                </div>
              </div>

              <!-- Buttons positioned below loan calculation details -->
              <div class="transaction-form-actions">
                <ion-button 
                  v-if="currentEvent" 
                  class="delete-button" 
                  @click="removeEvent"
                >
                  Delete
                </ion-button>
                <ion-button type="submit" class="save-button">
                  {{ currentEvent ? 'Update' : 'Save' }}
                </ion-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: Regular Transaction Details (For non-loan categories) -->
        <div v-else-if="newEvent.category && !isLoanCategory" class="transaction-step">
          <div class="step-header">
            <div class="step-number">2</div>
            <h3>Transaction Details</h3>
          </div>
          
          <div class="step-content">
            <div class="non-loan-form-layout">
              <!-- Main Form Fields in 3-column grid -->
              <div class="non-loan-fields-grid">
                <!-- Row 1: Name, Description, Amount -->
                <div class="transaction-input-group">
                  <ion-label>Name</ion-label>
                  <input 
                    type="text" 
                    v-model="newEvent.name" 
                    placeholder="Enter name" 
                    required
                    class="transaction-custom-input"
                  />
                </div>

                <div class="transaction-input-group">
                  <ion-label>Description</ion-label>
                  <input 
                    type="text" 
                    v-model="newEvent.description" 
                    placeholder="Enter description" 
                    required
                    class="transaction-custom-input"
                  />
                </div>

                <div class="transaction-input-group">
                  <ion-label>Amount ($)</ion-label>
                  <input 
                    type="text" 
                    :value="formattedAmount" 
                    @input="onAmountInput"
                    @blur="onAmountBlur"
                    placeholder="Enter amount" 
                    inputmode="decimal"
                    required
                    class="transaction-custom-input"
                  />
                </div>

                <!-- Row 2: Frequency, Type, Scenario -->
                <div class="transaction-input-group">
                  <ion-label>Frequency</ion-label>
                  <select 
                    v-model="newEvent.frequency" 
                    class="transaction-custom-input"
                  >
                    <option value="ONCE">Once</option>
                    <option value="DAILY">Daily</option>
                    <option value="EVERY_OTHER_DAY">Every Other Day</option>
                    <option value="WEEKDAYS_ONLY">Weekdays Only</option>
                    <option value="WEEKENDS_ONLY">Weekends Only</option>
                    <option value="WEEKLY">Weekly</option>
                    <option value="EVERY_OTHER_WEEK">Every Other Week</option>
                    <option value="MONTHLY">Monthly</option>
                    <option value="EVERY_OTHER_MONTH">Every Other Month</option>
                    <option value="TWICE_PER_YEAR">Twice Per Year</option>
                    <option value="YEARLY">Yearly</option>
                  </select>
                </div>

                <div class="transaction-input-group">
                  <ion-label>Type</ion-label>
                  <select 
                    v-model="newEvent.type" 
                    class="transaction-custom-input"
                  >
                    <option value="CREDIT">Income</option>
                    <option value="DEBIT">Expense</option>
                  </select>
                </div>

                <div class="transaction-input-group">
                  <ion-label>Scenario</ion-label>
                  <select 
                    v-model="newEvent.scenarioID" 
                    class="transaction-custom-input"
                  >
                    <option value="" disabled selected>Select scenario</option>
                    <option 
                      v-for="scenario in scenarios" 
                      :key="scenario.id" 
                      :value="scenario.id"
                    >
                      {{ scenario.name }}
                    </option>
                  </select>
                </div>

                <!-- Row 3: Start Date, End Date, Monthly Amount (if applicable) -->
                <div class="transaction-input-group">
                  <ion-label>Start Date</ion-label>
                  <input 
                    type="date" 
                    v-model="newEvent.startDate" 
                    :min="minDate"
                    :max="maxDate"
                    required
                    class="transaction-custom-input"
                  />
                </div>

                <div class="transaction-input-group">
                  <ion-label>End Date</ion-label>
                  <input 
                    type="date" 
                    v-model="newEvent.endDate" 
                    :min="minDate"
                    :max="maxDate"
                    required
                    class="transaction-custom-input"
                  />
                </div>

                <div v-if="shouldShowMonthlyAmount" class="transaction-input-group">
                  <ion-label>Monthly Amount ($)</ion-label>
                  <input 
                    type="number" 
                    v-model="newEvent.monthlyAmount" 
                    placeholder="Enter monthly amount" 
                    step="0.01"
                    inputmode="decimal"
                    @blur="onDecimalBlur('monthlyAmount', $event)"
                    class="transaction-custom-input"
                  />
                </div>

                <!-- Empty div for spacing when monthly amount is not shown -->
                <div v-if="!shouldShowMonthlyAmount" class="transaction-input-group">
                  <!-- Empty space for alignment -->
                </div>
              </div>

              <!-- Buttons -->
              <div class="transaction-form-actions">
                <ion-button 
                  v-if="currentEvent" 
                  class="delete-button" 
                  @click="removeEvent"
                >
                  Delete
                </ion-button>
                <ion-button type="submit" class="save-button">
                  {{ currentEvent ? 'Update' : 'Save' }}
                </ion-button>
              </div>
            </div>
          </div>
        </div>
      </form>


      <!-- Toast Notification -->
      <div v-if="showToast" class="toast-notification" :class="toastType">
        <div class="toast-content">
          <ion-icon :icon="toastIcon" class="toast-icon"></ion-icon>
          <span class="toast-message">{{ toastMessage }}</span>
        </div>
        <ion-button fill="clear" class="toast-close" @click="hideToast">
          <ion-icon :icon="close"></ion-icon>
        </ion-button>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import {
  IonPage, IonContent, IonButton, IonLabel, IonIcon,
  IonDatetime, useIonRouter
} from '@ionic/vue';
import { chevronBack, checkmark, close, alertCircle } from 'ionicons/icons';
import axios from 'axios';
import { useCalendarStore } from '@/store/calendar';
import { useEventsStore } from '@/store/events';
import { mapState, mapActions } from 'pinia';
import {getAPIURL} from '../logic/api';

export default {
  name: 'AddTransactionPage',
  components: {
    IonPage, IonContent, IonButton, IonLabel, IonIcon,
    IonDatetime
  },
  setup() {
    const store = useCalendarStore();
    const router = useIonRouter();
    return { store, router };
  },
  data() {
    return {
      months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      years: Array.from({ length: 6 }, (_, i) => i + 2020),
      minDate: new Date('2000-01-01').toISOString(),
      maxDate: new Date(new Date().getFullYear() + 80, 11, 31).toISOString(),
      profileID: null,
      scenarioID: null,
      day: null,
      eventID: null,
      chevronBack,
      checkmark,
      close,
      alertCircle,
      showToast: false,
      toastMessage: '',
      toastType: 'success',
      toastIcon: checkmark,
      currentEvent: null, // Add this to store the current event being edited
      isLoadingEvent: false, // Add this to track loading state
      loanCalculationPreview: null, // Store loan calculation preview
      loanCalculationTimeout: null, // Debounce timer for loan calculations
      newEvent: {
        name: '',
        description: '',
        type: 'DEBIT',
        category: '',
        frequency: 'MONTHLY',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        amount: '',
        active: true,
        profileID: null,
        scenarioID: null,
        eventID: null,
        principal: '',
        interest: '',
        monthlyAmount: '',
        loanTerm: '',
        escrow: ''
      }
    }
  },
  computed: {
    ...mapState(useCalendarStore, ['events', 'selectedScenario', 'scenarios']),
    isLoanCategory() {
      const category = this.newEvent?.category;
      const result = category === 'MORTGAGE' || category === 'GENERIC_LOAN' || category === 'AUTO_LOAN';
      return result;
    },
    shouldShowMonthlyAmount() {
      const category = this.newEvent?.category;
      return category === 'MORTGAGE' || category === 'GENERIC_LOAN' || category === 'AUTO_LOAN';
    },
    amountLabel() {
      return this.isLoanCategory ? 'Total Loan Amount ($)' : 'Amount ($)';
    },
    formattedAmount() {
      if (!this.newEvent.amount || this.newEvent.amount === '') return '';
      // Just return the amount as-is, no formatting
      return this.newEvent.amount;
    }
  },
  methods: {
    loadCurrentEventData() {
      
      // If we have a currentEvent loaded, populate newEvent with its data
      if (this.currentEvent) {
        this.newEvent = {
          name: this.currentEvent.name,
          description: this.currentEvent.description,
          type: this.currentEvent.type,
          category: this.currentEvent.category,
          frequency: this.currentEvent.frequency,
          startDate: new Date(this.currentEvent.start_date).toISOString().split('T')[0],
          endDate: new Date(this.currentEvent.end_date).toISOString().split('T')[0],
          amount: this.currentEvent.amount,
          active: this.currentEvent.active,
          profileID: this.currentEvent.profile_id,
          scenarioID: this.currentEvent.scenario_id,
          eventID: this.currentEvent.id,
          principal: this.currentEvent.principal ?? '',
          interest: this.currentEvent.interest ?? '',
          monthlyAmount: this.currentEvent.monthly_payment ?? '',
          loanTerm: this.currentEvent.term ? String(this.currentEvent.term) : '',
          escrow: this.currentEvent.escrow ?? ''
        };
        
        // Trigger loan calculation if this is a loan category with all required fields
        if (this.isLoanCategory && this.newEvent.amount && this.newEvent.interest && this.newEvent.loanTerm) {
          this.debouncedCalculateLoan();
        }
      } else {
        // For new events, set up default scenario
        let scenarioID = this.selectedScenario ? this.selectedScenario.id : this.scenarioID;
        
        // If no scenario is selected and scenarios are available, default to the first one
        if (!scenarioID && this.scenarios && this.scenarios.length > 0) {
          // Try to find "Default" scenario first, otherwise use the first scenario
          const defaultScenario = this.scenarios.find(scenario => 
            scenario.name && scenario.name.toLowerCase() === 'default'
          );
          scenarioID = defaultScenario ? defaultScenario.id : this.scenarios[0].id;
        }
        
        this.newEvent.scenarioID = scenarioID;
        this.newEvent.profileID = this.profileID;
        this.newEvent.eventID = this.eventID;
        
      }
      console.log('=== END LOADING EVENT DATA ===');
    },
    
    onAmountInput(event) {
      const value = event.target.value;
      // Only remove non-numeric characters except decimal point, allow user to type freely
      const raw = value.replace(/[^0-9.]/g, '');
      
      // Keep only one decimal point
      const parts = raw.split('.');
      if (parts.length > 2) {
        parts.splice(2);
      }
      
      const sanitized = parts.join('.');
      this.newEvent.amount = sanitized;
      
      // Trigger validation and loan calculation
      if (this.isLoanCategory) {
        this.validateLoanAmounts();
        this.debouncedCalculateLoan();
      }
    },
    
    onAmountBlur(event) {
      const value = event.target.value;
      if (value === '' || value === null || value === undefined) return;
      
      // Just sanitize the input, don't force any formatting
      const raw = value.replace(/[^0-9.]/g, '');
      const parts = raw.split('.');
      
      // Keep only one decimal point
      if (parts.length > 2) {
        parts.splice(2);
      }
      
      const sanitized = parts.join('.');
      this.newEvent.amount = sanitized;
      
      // Trigger validation for loan amount comparison
      if (this.isLoanCategory) {
        this.validateLoanAmounts();
      }
    },
    
    onDecimalBlur(field, event) {
      const value = event.target.value;
      if (value === '' || value === null || value === undefined) return;
      
      // Sanitize the input: remove non-numeric characters except decimal point
      const raw = value.replace(/[^0-9.]/g, '');
      const parts = raw.split('.');
      
      // Keep only one decimal point
      if (parts.length > 2) {
        parts.splice(2);
      }
      
      // Limit decimal places based on field type
      let decimalPlaces = 2; // default for most fields
      if (field === 'interest') {
        decimalPlaces = 3; // allow 3 decimal places for interest rate
      }
      
      if (parts[1] !== undefined) {
        parts[1] = parts[1].slice(0, decimalPlaces);
      }
      
      const sanitized = parts.join('.');
      const num = Number(sanitized);
      
      if (!isNaN(num)) {
        this.newEvent[field] = num.toFixed(decimalPlaces);
      } else {
        this.newEvent[field] = sanitized;
      }
      
      // Trigger validation for loan amount comparison
      if (this.isLoanCategory && (field === 'amount' || field === 'principal')) {
        this.validateLoanAmounts();
      }
      
      // Clear validation for interest field when user types
      if (field === 'interest' && this.isLoanCategory) {
        const interestInput = event.target;
        if (interestInput && this.newEvent.interest && !isNaN(Number(this.newEvent.interest)) && Number(this.newEvent.interest) > 0) {
          interestInput.setCustomValidity('');
        }
      }

      // Trigger loan calculation for loan categories
      if (this.isLoanCategory && (field === 'amount' || field === 'principal' || field === 'interest')) {
        this.debouncedCalculateLoan();
      }
    },
    onInterestInvalid(event) {
      if (this.isLoanCategory) {
        const value = this.newEvent.interest;
        if (!value || value === '' || isNaN(Number(value)) || Number(value) <= 0) {
          event.target.setCustomValidity('Interest is needed to calculate this loan type');
        } else {
          event.target.setCustomValidity('');
        }
      } else {
        event.target.setCustomValidity('');
      }
    },
    onAmountInvalid(event) {
      if (this.isLoanCategory && this.newEvent.principal && this.newEvent.principal !== '') {
        const amount = parseFloat(this.newEvent.amount) || 0;
        const principal = parseFloat(this.newEvent.principal) || 0;
        if (amount <= principal) {
          event.target.setCustomValidity('Total loan amount must be greater than principal amount');
        } else {
          event.target.setCustomValidity('');
        }
      } else {
        event.target.setCustomValidity('');
      }
    },
    onPrincipalInvalid(event) {
      if (this.isLoanCategory && this.newEvent.amount && this.newEvent.amount !== '') {
        const amount = parseFloat(this.newEvent.amount) || 0;
        const principal = parseFloat(this.newEvent.principal) || 0;
        if (principal >= amount) {
          event.target.setCustomValidity('Principal amount must be less than total loan amount');
        } else {
          event.target.setCustomValidity('');
        }
      } else {
        event.target.setCustomValidity('');
      }
    },
    validateLoanAmounts() {
      if (!this.isLoanCategory) return;
      
      const amountInput = this.$el.querySelector('input[v-model="newEvent.amount"]');
      const principalInput = this.$el.querySelector('input[v-model="newEvent.principal"]');
      
      if (amountInput && principalInput) {
        const amount = parseFloat(this.newEvent.amount) || 0;
        const principal = parseFloat(this.newEvent.principal) || 0;
        
        if (this.newEvent.principal && this.newEvent.principal !== '' && amount <= principal) {
          amountInput.setCustomValidity('Total loan amount must be greater than principal amount');
        } else {
          amountInput.setCustomValidity('');
        }
        
        if (this.newEvent.amount && this.newEvent.amount !== '' && principal >= amount) {
          principalInput.setCustomValidity('Principal amount must be less than total loan amount');
        } else {
          principalInput.setCustomValidity('');
        }
      }
    },
    onTermChange() {
      if (this.isLoanCategory && this.newEvent.loanTerm && this.newEvent.startDate) {
        // Calculate end date based on term and start date
        const startDate = new Date(this.newEvent.startDate);
        const termInMonths = parseInt(this.newEvent.loanTerm);
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + termInMonths);
        
        // Update the end date
        this.newEvent.endDate = endDate.toISOString();
        
        // Trigger loan calculation
        this.debouncedCalculateLoan();
      }
    },
    ...mapActions(useCalendarStore, ['fetchEvents', 'fetchEventsForMonthByScenario', 'fetchScenarios']),
    ...mapActions(useEventsStore, ['fetchEventById']),
    
    goBack() {
      this.$router.go(-1);
    },
    
    showSuccessToast(message) {
      this.toastMessage = message;
      this.toastType = 'success';
      this.toastIcon = this.checkmark;
      this.showToast = true;
      setTimeout(() => {
        this.hideToast();
      }, 4000);
    },
    showErrorToast(message) {
      this.toastMessage = message;
      this.toastType = 'error';
      this.toastIcon = this.alertCircle;
      this.showToast = true;
      setTimeout(() => {
        this.hideToast();
      }, 5000);
    },
    hideToast() {
      this.showToast = false;
    },
    
    async saveEvent() {
      // Format dates before sending to API
      const formattedStartDate = this.newEvent.startDate.split('T')[0];
      
      // For loan categories, use calculated end date if available, otherwise use user input
      let formattedEndDate;
      if (this.isLoanCategory && this.loanCalculationPreview && this.loanCalculationPreview.endDate) {
        // Convert loan calculation end date to YYYY-MM-DD format
        formattedEndDate = new Date(this.loanCalculationPreview.endDate).toISOString().split('T')[0];
      } else {
        formattedEndDate = this.newEvent.endDate.split('T')[0];
      }



      try {
        // Validate interest when loan category
        if (this.isLoanCategory && (!this.newEvent.interest || this.newEvent.interest === '' || isNaN(Number(this.newEvent.interest)) || Number(this.newEvent.interest) <= 0)) {
          this.showErrorToast('Interest is required for loan categories.');
          return;
        }
        
        // Validate loan amounts
        if (this.isLoanCategory && this.newEvent.principal && this.newEvent.principal !== '') {
          const amount = parseFloat(this.newEvent.amount) || 0;
          const principal = parseFloat(this.newEvent.principal) || 0;
          if (amount <= principal) {
            this.showErrorToast('Total loan amount must be greater than principal amount.');
            return;
          }
        }
        // Use currentEvent.id if editing an existing event, otherwise use newEvent.eventID
        const eventID = this.currentEvent ? this.currentEvent.id : this.newEvent.eventID;
        
        if (eventID) {
          // Updating existing event
          await axios.put(getAPIURL()+'/api/scenario/update-event', {
            ...this.newEvent,
            eventID: eventID, // Ensure we pass the correct event ID
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            amount: parseFloat(this.newEvent.amount),
            active: this.newEvent.active,
            principal: this.newEvent.principal === '' || this.newEvent.principal === null ? null : parseFloat(this.newEvent.principal),
            interest: this.newEvent.interest === '' || this.newEvent.interest === null ? null : parseFloat(this.newEvent.interest),
            escrow: this.newEvent.escrow === '' || this.newEvent.escrow === null ? null : parseFloat(this.newEvent.escrow),
            calculatedEndDate: this.isLoanCategory && this.loanCalculationPreview ? 
              new Date(this.loanCalculationPreview.endDate).toISOString().split('T')[0] : 
              formattedEndDate,
            monthlyPayment: this.isLoanCategory && this.loanCalculationPreview ? 
              parseFloat(this.loanCalculationPreview.monthlyPayment) : 
              (this.newEvent.monthlyAmount === '' || this.newEvent.monthlyAmount === null ? null : parseFloat(this.newEvent.monthlyAmount)),
            term: this.newEvent.loanTerm === '' || this.newEvent.loanTerm === null ? null : parseInt(this.newEvent.loanTerm),
          });
          
          // Log successful update
          console.log('=== EVENT UPDATE SUCCESS ===');
          
          // Emit event bus event to notify other components
          this.$eventBus.emit('transaction-updated', { eventId: eventID });
          
          this.showSuccessToast('Transaction updated successfully!');
        } else {
          // Creating new event
          const response = await axios.post(getAPIURL()+'/api/scenario/create-event', {
            ...this.newEvent,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            amount: parseFloat(this.newEvent.amount),
            active: this.newEvent.active,
            principal: this.newEvent.principal === '' || this.newEvent.principal === null ? null : parseFloat(this.newEvent.principal),
            interest: this.newEvent.interest === '' || this.newEvent.interest === null ? null : parseFloat(this.newEvent.interest),
            escrow: this.newEvent.escrow === '' || this.newEvent.escrow === null ? null : parseFloat(this.newEvent.escrow),
            calculatedEndDate: this.isLoanCategory && this.loanCalculationPreview ? 
              new Date(this.loanCalculationPreview.endDate).toISOString().split('T')[0] : 
              formattedEndDate,
            monthlyPayment: this.isLoanCategory && this.loanCalculationPreview ? 
              parseFloat(this.loanCalculationPreview.monthlyPayment) : 
              (this.newEvent.monthlyAmount === '' || this.newEvent.monthlyAmount === null ? null : parseFloat(this.newEvent.monthlyAmount)),
            loanTerm: this.newEvent.loanTerm === '' || this.newEvent.loanTerm === null ? null : parseInt(this.newEvent.loanTerm),
          });
          
          // Log successful creation
          console.log('=== EVENT CREATION SUCCESS ===');
          
          // Emit event bus event to notify other components
          this.$eventBus.emit('transaction-created', { eventId: response?.data?.id || response?.data?._id });
          
          this.showSuccessToast('Transaction created successfully!');
        }
        
        // Navigate back to calendar immediately after successful save
        this.$router.replace({
          path: '/calendar',
          query: {
            profileID: this.newEvent.profileID,
            scenarioID: this.newEvent.scenarioID,
            refresh: Date.now() // Add timestamp to force refresh
          }
        });
      } catch (error) {
        
        this.showErrorToast('Failed to save transaction. Please try again.');
      }
    },
    
    async removeEvent() {
      try {
        // Use currentEvent.id if editing an existing event, otherwise use newEvent.eventID
        const eventID = this.currentEvent ? this.currentEvent.id : this.newEvent.eventID;
        
        await axios.delete(getAPIURL()+'/api/scenario/remove-event', {
          params: {
            eventID: eventID,
            scenarioID: this.newEvent.scenarioID,
            profileID: this.newEvent.profileID
          }
        });
        
        // Emit event bus event to notify other components
        this.$eventBus.emit('transaction-deleted', { eventId: eventID });
        
        this.showSuccessToast('Transaction deleted successfully!');
        
        // Navigate back to calendar immediately after successful delete
        this.$router.replace({
          path: '/calendar',
          query: {
            profileID: this.newEvent.profileID,
            scenarioID: this.newEvent.scenarioID,
            refresh: Date.now() // Add timestamp to force refresh
          }
        });
      } catch (error) {
        
        this.showErrorToast('Failed to delete transaction. Please try again.');
      }
    },
    
    updateDate() {
      const date = new Date(this.dateComponents.year, this.dateComponents.month, this.dateComponents.day);
      this.newEvent.date = date;
    },
    
    handleCategoryChange(value) {
      if (value === 'SAVINGS') {
        this.newEvent.type = 'DEBIT';
      }
    },
    
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    },
    
    getScenarioName(scenarioID) {
      if (!scenarioID || !this.scenarios) return 'Unknown';
      const scenario = this.scenarios.find(s => s.id === scenarioID);
      return scenario ? scenario.name : 'Unknown';
    },
    
    debouncedCalculateLoan() {
      // Clear existing timeout
      if (this.loanCalculationTimeout) {
        clearTimeout(this.loanCalculationTimeout);
      }
      
      // Set new timeout for 500ms delay
      this.loanCalculationTimeout = setTimeout(() => {
        this.calculateLoanDetails();
      }, 500);
    },
    
    async calculateLoanDetails() {
      if (!this.isLoanCategory) {
        this.loanCalculationPreview = null;
        return;
      }

      const totalLoanAmount = parseFloat(this.newEvent.amount) || 0;
      const additionalPrincipalPayment = parseFloat(this.newEvent.principal) || 0;
      const interestRate = parseFloat(this.newEvent.interest) || 0;
      const startDate = this.newEvent.startDate;
      const loanTerm = this.newEvent.loanTerm;

      // Validate required fields
      if (!totalLoanAmount || !interestRate || !startDate || !loanTerm) {
        this.loanCalculationPreview = null;
        return;
      }

      try {
        const response = await axios.post(getAPIURL() + '/api/scenario/calculate-loan-details', {
          totalLoanAmount,
          additionalPrincipalPayment,
          interestRate,
          startDate: startDate.split('T')[0], // Convert to YYYY-MM-DD format
          loanTerm: parseInt(loanTerm) // Send term in months
        });

        if (response.data) {
          this.loanCalculationPreview = response.data;
          
          // Force Vue to re-render
          this.$forceUpdate();
          
          // Auto-update the end date if it's not manually set
          if (!this.currentEvent || this.newEvent.endDate === this.currentEvent.end_date) {
            this.newEvent.endDate = new Date(response.data.endDate).toISOString();
          }
        }
      } catch (error) {
        this.loanCalculationPreview = null;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        return 'Invalid Date';
      }
    },
  },
  async mounted() {
    this.eventID = this.$route.query.eventID;
    this.profileID = this.$route.query.profileID;
    this.scenarioID = this.$route.query.scenarioID;
    
    // Fetch scenarios if not already loaded
    if (!this.scenarios || this.scenarios.length === 0) {
      await this.fetchScenarios();
    }
    
    // If no scenarioID is provided in the route, use the currently selected scenario from the store
    if (!this.scenarioID && this.selectedScenario) {
      this.scenarioID = this.selectedScenario.id;
    }
    
    // If we have an eventID, fetch the specific event first
    if (this.eventID) {
      console.log('=== FETCHING EVENT ===');
      this.isLoadingEvent = true;
      try {
        const event = await this.fetchEventById(this.eventID);
        if (event) {
          this.currentEvent = event;
        } else {
          this.showErrorToast('Failed to load event details. Please try again.');
        }
      } catch (error) {
        this.showErrorToast('Failed to load event details. Please try again.');
      } finally {
        this.isLoadingEvent = false;
      }
      console.log('=== END FETCHING EVENT ===');
    }
    
    // Load current event data (either from fetched event or initialize for new event)
    this.loadCurrentEventData();
  },
  watch: {
    // Watch category to enforce validation state and trigger loan calculations
    'newEvent.category': function(newVal) {
      if (!this.isLoanCategory && (this.newEvent && this.newEvent.interest === '')) return;
      
      // Clear loan calculation preview when switching away from loan categories
      if (!this.isLoanCategory) {
        this.loanCalculationPreview = null;
      } else {
        // Trigger loan calculation when switching to loan category
        this.debouncedCalculateLoan();
      }
    },
    // Watch for changes in loan-related fields to trigger calculation
    'newEvent.amount': function() {
      if (this.isLoanCategory && this.newEvent.amount && this.newEvent.interest && this.newEvent.loanTerm) {
        this.debouncedCalculateLoan();
      }
    },
    'newEvent.principal': function() {
      if (this.isLoanCategory && this.newEvent.amount && this.newEvent.interest && this.newEvent.loanTerm) {
        this.debouncedCalculateLoan();
      }
    },
    'newEvent.interest': function() {
      if (this.isLoanCategory && this.newEvent.amount && this.newEvent.interest && this.newEvent.loanTerm) {
        this.debouncedCalculateLoan();
      }
    },
    'newEvent.startDate': function() {
      if (this.isLoanCategory && this.newEvent.amount && this.newEvent.interest && this.newEvent.loanTerm) {
        this.debouncedCalculateLoan();
      }
    },
    'newEvent.endDate': function() {
      if (this.isLoanCategory && this.newEvent.amount && this.newEvent.interest) {
        this.debouncedCalculateLoan();
      }
    },
    'newEvent.loanTerm': function() {
      if (this.isLoanCategory && this.newEvent.amount && this.newEvent.interest && this.newEvent.loanTerm) {
        // Calculate end date from term and trigger calculation
        this.onTermChange();
      }
    },
    'newEvent.escrow': function() {
      // Escrow changes don't affect loan calculation, but we need to trigger UI update
      if (this.isLoanCategory) {
        this.$forceUpdate();
      }
    },
    selectedScenario: {
      handler(newScenario) {
        // Update local scenarioID if selectedScenario changes and we don't have a scenarioID from route
        if (newScenario && !this.$route.query.scenarioID) {
          this.scenarioID = newScenario.id;
        }
      },
      immediate: true
    }
  }
}
</script>